import { useState, useMemo } from 'react';
import { Copy } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

const KEYWORDS = [
  'SELECT', 'FROM', 'WHERE', 'JOIN', 'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN',
  'ON', 'AND', 'OR', 'NOT', 'IN', 'IS', 'NULL', 'GROUP BY', 'ORDER BY', 'HAVING',
  'LIMIT', 'OFFSET', 'INSERT INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE FROM',
  'CREATE TABLE', 'ALTER TABLE', 'DROP TABLE', 'AS', 'WITH', 'UNION', 'UNION ALL',
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'ASC', 'DESC', 'LIKE', 'BETWEEN', 'EXISTS',
  'DISTINCT',
];

function format(sql: string): string {
  let out = sql.trim();
  // Normalize whitespace
  out = out.replace(/\s+/g, ' ');
  // Uppercase keywords
  for (const kw of KEYWORDS) {
    const re = new RegExp(`\\b${kw.replace(/\s+/g, '\\s+')}\\b`, 'gi');
    out = out.replace(re, kw);
  }
  // Newlines before major keywords
  const breakers = ['SELECT', 'FROM', 'WHERE', 'GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT', 'JOIN', 'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN', 'UNION', 'UNION ALL', 'WITH', 'INSERT INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE FROM'];
  breakers.forEach((kw) => {
    out = out.replace(new RegExp(`\\s${kw}\\b`, 'g'), `\n${kw}`);
  });
  // Indent AND/OR and ON
  out = out.replace(/\b(AND|OR|ON)\b/g, '\n  $1');
  // Comma at line start for multi-column
  out = out.replace(/,\s*/g, ',\n  ');
  return out;
}

const SAMPLE = `select id, name, email from users u left join orders o on o.user_id = u.id where u.active = true and o.total > 1000 order by u.created_at desc limit 10;`;

export function SqlFormat() {
  const [input, setInput] = useState(SAMPLE);
  const formatted = useMemo(() => (input ? format(input) : ''), [input]);

  return (
    <div>
      <ToolHeader
        toolId="sql-format"
        title="SQL formatter"
        description="Reformat SQL with uppercase keywords, indentation, and line breaks."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            SQL
          </div>
          <Textarea
            mono
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="!border-0 !rounded-t-none min-h-[360px] !bg-transparent focus-visible:!ring-0"
          />
        </Card>
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted">Formatted</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                const ok = await copyToClipboard(formatted);
                if (ok) toast.success('Copied');
              }}
            >
              <Copy size={11} />
            </Button>
          </div>
          <pre className="font-mono text-[13px] leading-relaxed p-4 overflow-auto min-h-[360px]">
            <code>{formatted}</code>
          </pre>
        </Card>
      </div>
    </div>
  );
}

export default SqlFormat;
